import React, { useContext, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
	Box,
	Heading,
	Text,
	Button,
	OrderedList,
	ListItem,
	Link,
	Center,
	Input,
	VStack,
	Icon,
	Flex,
	List,
	ListIcon,
	useColorModeValue,
	Circle,
	useToast,
} from "@chakra-ui/react"
import { ArrowBackIcon, CheckIcon } from "@chakra-ui/icons"
import { Upload } from "lucide-react"
import { BulkUploadLoader } from "../../Components/BulkUploadLoader"
import { validateCsvHeaders } from "../../Helpers/BulkUploadCSVValidator"
import { processCSV } from "../../Helpers/BulkUploadCSVProcessor"
import { UserContext } from "../../Contexts/UserContext"
import { Partners } from "../../partnershipData"

export const JobRequestBulkUpload = () => {
	const navigate = useNavigate()
	const toast = useToast()
	const [isDragging, setIsDragging] = useState(false)
	const { userPlus } = useContext(UserContext)
	const partner = Partners.find((p) => p.id === userPlus?.partner)
	const [files, setFiles] = useState([])
	const [error, setError] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const fileInputRef = useRef(null)

	const borderColor = useColorModeValue("borderColor", "red.500")
	const dragBorderColor = useColorModeValue("blue.400", "blue.600")
	const dragBgColor = useColorModeValue("blue.50", "blue.900")
	const iconBgColor = useColorModeValue("borderColor", "red.800")
	const iconColor = useColorModeValue("white", "red.300")

	const handleDragOver = (e) => {
		e.preventDefault()
		setIsDragging(true)
	}

	const handleDragLeave = () => {
		setIsDragging(false)
	}

	const validateFiles = async (fileList) => {
		setError("") // Clear previous errors
		const allFiles = Array.from(fileList)

		// First check if we have more than 1 file
		if (allFiles.length > 1) {
			setError("Only one file can be uploaded at a time.")
			return []
		}

		// Then check if the file is a CSV
		const validFiles = allFiles.filter(
			(file) => file.type === "text/csv" || file.name.endsWith(".csv")
		)

		if (validFiles.length < allFiles.length) {
			setError("Only CSV files are allowed.")
			return []
		}

		// Only proceed with validation if we have valid files
		if (validFiles.length > 0) {
			try {
				const isValid = await validateCsvHeaders(validFiles[0])

				if (isValid === false) {
					setError(
						"This CSV file does not have the valid headers. Please check you are using the correct template."
					)
					return [] // Return empty array to indicate invalid file
				}

				// If we got here, the file is valid
				return validFiles
			} catch (error) {
				console.error("Error validating CSV:", error)
				setError("Error validating CSV file. Please try again.")
				return []
			}
		}

		return validFiles
	}

	const handleDrop = async (e) => {
		e.preventDefault()
		setIsDragging(false)

		// Replace existing file with the new one
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const validFiles = await validateFiles(e.dataTransfer.files)
			if (validFiles.length > 0) {
				setFiles(validFiles)
			}
		}
	}

	const handleFileChange = async (e) => {
		// Replace existing file with the new one
		if (e.target.files && e.target.files.length > 0) {
			const validFiles = await validateFiles(e.target.files)
			if (validFiles.length > 0) {
				setFiles(validFiles)
			}
		}
	}

	const handleClick = () => {
		fileInputRef.current.click()
	}

	const handleUpload = async () => {
		if (files.length === 0) {
			setError("Please select a CSV file to upload.")
			return
		}

		setIsLoading(true)
		setError("")

		try {
			const results = await processCSV(files[0], userPlus)

			// Navigate to confirmation page with results
			navigate("/job-requests/bulk-upload/confirmation", {
				state: { results },
			})
		} catch (error) {
			console.error("Error processing CSV:", error)
			setError("Failed to process CSV file: " + error.message)
			toast({
				title: "Error",
				description: "Failed to process CSV file",
				status: "error",
				duration: 5000,
				isClosable: true,
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Box maxW="lg" mx="auto" p={6}>
			<Heading size="lg" fontWeight="bold" mb="25px">
				Bulk Upload Work Order
			</Heading>
			{!isLoading && (
				<>
					<OrderedList spacing={2} mb={6}>
						<ListItem>
							<Text>
								Format them using{" "}
								<Link
									href="https://docs.google.com/spreadsheets/d/1SM2UrcW5RF_rOAM4J7GRYW-AdZnuo5jDa3dlN6ElFPA/edit?usp=sharing"
									color="blue.600"
									textDecoration="underline"
									isExternal
								>
									Black Dome Services' spreadsheet template
								</Link>
							</Text>
						</ListItem>
						<ListItem>
							<Text>Save the file as a .CSV</Text>
						</ListItem>
						<ListItem>
							<Text>Upload it to Black Dome Services</Text>
						</ListItem>
					</OrderedList>

					<Text mb={6}>
						Black Dome Services will then create and submit each work order to
						PPW automatically.
					</Text>

					<Button
						leftIcon={<ArrowBackIcon />}
						variant="ghost"
						mb={6}
						pl={0}
						onClick={() => {
							navigate("/job-requests/new")
						}}
					>
						Back
					</Button>

					<Box
						borderWidth="2px"
						borderStyle="dashed"
						borderColor={isDragging ? dragBorderColor : borderColor}
						borderRadius="lg"
						p={8}
						textAlign="center"
						bg={isDragging ? dragBgColor : "transparent"}
						cursor="pointer"
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						onClick={handleClick}
						transition="all 0.2s"
					>
						<Center mb={4}>
							<Circle size="12" bg={iconBgColor}>
								<Icon as={Upload} color={iconColor} boxSize={6} />
							</Circle>
						</Center>

						<VStack spacing={2}>
							<Text fontSize="lg" color="gray.600">
								Upload or drag and drop a CSV file
							</Text>

							<Text color="gray.500" mb={4}>
								Max. File Size: 30MB
							</Text>

							<Button variant="outline" colorScheme="gray" bg="white">
								Add CSV file
							</Button>
						</VStack>

						<Input
							type="file"
							ref={fileInputRef}
							onChange={handleFileChange}
							accept=".csv,text/csv"
							display="none"
						/>
					</Box>

					{error && (
						<Box mt={4} p={3} bg="red.50" borderRadius="md">
							<Text color="red.500" fontWeight="medium">
								{error}
							</Text>
						</Box>
					)}

					{files.length > 0 && (
						<Box mt={4}>
							<Text fontWeight="medium" mb={2}>
								Selected file:
							</Text>
							<List>
								<ListItem>
									<Flex alignItems="center">
										<ListIcon as={CheckIcon} color="green.500" />
										<Text>{files[0].name}</Text>
									</Flex>
								</ListItem>
							</List>
						</Box>
					)}
					<Button
						onClick={handleUpload}
						bgColor="borderColor"
						color="white"
						w="100%"
						mt="40px"
						mb="40px"
						isDisabled={files.length === 0}
					>
						Create {partner ? partner.jobRequestHeader : "Job Requests"}
					</Button>
				</>
			)}
			{isLoading && <BulkUploadLoader />}
		</Box>
	)
}
